import React, { useEffect, useMemo, useState, useCallback } from "react";
import "../styles/db.css";
import { Button, Row, Table, Spin } from "antd";
import {
  ArrowRightOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  FileTextOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import PageHeader from "../components/generic/PageHeader";
import { useLocation, useNavigate } from "react-router-dom";
import { job_queue_summary_column } from "../constants/Table Columns";
import { getAllJobs } from "../service/run.service";
import { getMasterSuiteDetailResultByJob } from "../service/result.service";
import RunStatusBadge from "../components/dashboard/RunStatusBadge";
import StatStrip from "../components/dashboard/StatStrip";
import CustomLoadingIcon from "../components/generic/CustomLoadingIcon";
import { parseDateString, formatDuration } from "../helper/date_helper";
import { navigateToStepResult } from "../helper/stepResultNavigation";

const RUNNING_STATES = ["In Progress", "Queued"];
const sequenceOf = (name) => parseInt(name?.split(".")[0] || "0", 10);
const stepNameOf = (name) => name?.split(".")[1] || name || "";

function JobDetail() {
  const navigate = useNavigate();
  const location = useLocation();

  const jobId =
    location.state?.jobId ||
    new URLSearchParams(location.search).get("jobId");

  const [jobs, setJobs] = useState([]);
  const [resultBySeq, setResultBySeq] = useState({});
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());

  // ─── Fetch the flat job list (parent + children all in one) ───
  const fetchJobs = useCallback(() => {
    getAllJobs()
      .then((res) => setJobs(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // ─── Fetch per-step results → map by step sequence number ─────
  // A step's result is written as it completes, so this fills in over
  // the life of the run; we key by sequence to match the job steps.
  const fetchStepResults = useCallback(() => {
    if (!jobId) return;
    getMasterSuiteDetailResultByJob(jobId)
      .then((res) => {
        const map = {};
        (res.data || []).forEach((r) => {
          if (r.stepSequenceNumber != null) {
            map[r.stepSequenceNumber] = {
              stepResultId: r.stepResultId,
              stepTestResult: r.stepTestResult,
            };
          }
        });
        setResultBySeq(map);
      })
      .catch(() => {});
  }, [jobId]);

  useEffect(() => {
    fetchJobs();
    fetchStepResults();
  }, [fetchJobs, fetchStepResults]);

  // ─── Derive the suite + its steps ─────────────────────────────
  const parent = useMemo(
    () => jobs.find((j) => j.jobId === jobId),
    [jobs, jobId]
  );

  const steps = useMemo(
    () =>
      jobs
        .filter((j) => j.parentJobId === jobId)
        .sort((a, b) => sequenceOf(a.name) - sequenceOf(b.name)),
    [jobs, jobId]
  );

  const isRunning =
    parent && RUNNING_STATES.includes(parent.jobStatus);

  // ─── Poll while running so an in-flight suite stays live ──────
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => {
      fetchJobs();
      fetchStepResults();
    }, 5000);
    return () => clearInterval(id);
  }, [isRunning, fetchJobs, fetchStepResults]);

  // ─── 1s ticker while running ──────────────────────────────────
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  // ─── Telemetry ────────────────────────────────────────────────
  const telemetry = useMemo(() => {
    const total = steps.length;
    const doneSteps = steps.filter(
      (s) => !RUNNING_STATES.includes(s.jobStatus)
    );
    const done = doneSteps.length;
    const remaining = total - done;
    const pctComplete = total ? Math.round((done / total) * 100) : 0;

    const currentStep = steps.find((s) => s.jobStatus === "In Progress");

    // Average duration of steps that have both timestamps → ETA
    const timed = doneSteps
      .map((s) => parseDateString(s.runEnd) - parseDateString(s.runStart))
      .filter((d) => d > 0);
    const avg = timed.length
      ? timed.reduce((a, d) => a + d, 0) / timed.length
      : 0;
    const eta = avg * remaining;

    return { total, done, remaining, pctComplete, currentStep, eta };
  }, [steps]);

  // ─── Elapsed / duration ticker text ───────────────────────────
  const start = parent ? parseDateString(parent.runStart) : 0;
  const tickerMs = isRunning
    ? now - start
    : parent
    ? parseDateString(parent.runEnd) - start
    : 0;
  const tickerText = formatDuration(tickerMs);

  // ─── Render guards ────────────────────────────────────────────
  if (loading) {
    return (
      <div className="db-container">
        <div style={{ padding: "80px 0", textAlign: "center" }}>
          <Spin indicator={<CustomLoadingIcon />} />
        </div>
      </div>
    );
  }

  if (!parent) {
    return (
      <div className="db-container">
        <PageHeader
          backTo="/jobs"
          icon={<UnorderedListOutlined />}
          title="Job Details"
        />
        <div style={{ padding: "40px 0", textAlign: "center", color: "#6B7280" }}>
          Job{jobId ? ` "${jobId}"` : ""} could not be found. It may have been
          removed.
        </div>
      </div>
    );
  }

  const currentStepLabel = telemetry.currentStep
    ? `Step ${sequenceOf(telemetry.currentStep.name)} of ${telemetry.total} — ${stepNameOf(
        telemetry.currentStep.name
      )}`
    : telemetry.total && telemetry.pctComplete === 100
    ? "All steps complete"
    : "—";

  // StatStrip stats — Duration/ETA only (per selection); % complete drives the bar
  const stats = [
    {
      label: isRunning ? "Elapsed" : "Duration",
      value: tickerText,
      color: isRunning ? "#FCD34D" : "#86EFAC",
    },
    ...(isRunning && telemetry.eta > 0
      ? [{ label: "Est. remaining", value: `~${formatDuration(telemetry.eta)}` }]
      : []),
  ];

  // Steps table = shared job columns + a local "View result" action column.
  // Each step's result is keyed by sequence; the link appears once written.
  const stepColumns = [
    ...job_queue_summary_column,
    {
      title: "Result",
      key: "result",
      width: 130,
      align: "center",
      render: (_, record) => {
        const result = resultBySeq[sequenceOf(record.name)];
        return result?.stepResultId ? (
          <a
            onClick={() => navigateToStepResult(navigate, result)}
            style={{ color: "#185FA5", fontWeight: 600, whiteSpace: "nowrap" }}
          >
            View result <ArrowRightOutlined style={{ fontSize: 10 }} />
          </a>
        ) : (
          <span style={{ color: "#9CA3AF" }}>—</span>
        );
      },
    },
  ];

  return (
    <div className="db-container">
      {/* ─── Header ─────────────────────────────────────────── */}
      <PageHeader
        backTo="/jobs"
        icon={<UnorderedListOutlined />}
        title="Job Details"
        tooltip="Master Suite run breakdown"
        subtitle={
          <span style={{ fontFamily: "monospace" }}>
            {parent.jobId}
            {parent.name ? `  ·  ${parent.name}` : ""}
          </span>
        }
        actions={
          <>
            <RunStatusBadge status={parent.jobStatus} />
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={() =>
                navigate("/results/mastersuite", {
                  state: { key: parent.jobId },
                })
              }
            >
              View full report
            </Button>
          </>
        }
      />

      {/* ─── Telemetry strip ────────────────────────────────── */}
      <div style={{ marginBottom: 16 }}>
        <StatStrip
          stats={stats}
          passRate={telemetry.pctComplete}
          passRateLabel="Complete"
        />
      </div>

      {/* ─── Current step banner ────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 16px",
          marginBottom: 16,
          borderRadius: 10,
          border: "1px solid #E5E7EB",
          background: isRunning ? "#E6F1FB" : "#F9FAFB",
        }}
      >
        {isRunning ? (
          <SyncOutlined spin style={{ color: "#185FA5", fontSize: 15 }} />
        ) : (
          <ClockCircleOutlined style={{ color: "#6B7280", fontSize: 15 }} />
        )}
        <span style={{ fontSize: 12, color: "#6B7280", fontWeight: 600 }}>
          CURRENT STEP
        </span>
        <span style={{ fontSize: 14, fontWeight: 500, color: "#111827" }}>
          {currentStepLabel}
        </span>
      </div>

      {/* ─── Steps table ────────────────────────────────────── */}
      <Row className="table-container">
        <Table
          style={{ width: "100%" }}
          columns={stepColumns}
          dataSource={steps}
          rowKey="jobId"
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "30"],
          }}
        />
      </Row>
    </div>
  );
}

export default JobDetail;
